import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft, Video, ExternalLink, Copy, Check, Clock, Send,
  ClipboardList, BookOpen, Target, Users, MessageSquare, FileText, Pin, Lock
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { EmptyState } from '../components/ui/EmptyState';
import { useSupabaseClient } from '../hooks/useSupabaseClient';
import { useToast } from '../components/Toast';
import { getAssignmentUrl, formatTurkishDueDate } from '../utils/assignment';
import { formatStudentName, maskStudentName } from '../utils/student';
import { UpcomingAssignmentsCard } from '../components/UpcomingAssignmentsCard';
import { getClass3DIconPath, classInitials, extractIconFromDescription, getClassGradient } from '../utils/classIcon';
import { EmoteText } from '../components/EmoteText';
import { EmojiPickerButton } from '../components/EmojiPickerButton';
import { ReactionBar } from '../components/ReactionBar';
import type { ClassRow, AssignmentRow, LiveSessionRow, ClassStudentRow, ClassCommentRow, ClassMaterialRow } from '../types/sinif';

interface ClassMember {
  student_id: string;
  display_name: string;
}

type TabType = 'akis' | 'odevler' | 'materyaller' | 'kisiler';

export default function StudentClassDetail() {
  const { classId } = useParams<{ classId: string }>();
  const { user } = useUser();
  const getSupabase = useSupabaseClient();
  const { showToast } = useToast();

  const [classRow, setClassRow] = useState<ClassRow | null>(null);


  const [teacherName, setTeacherName] = useState('Öğretmen');
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [liveSessions, setLiveSessions] = useState<LiveSessionRow[]>([]);
  const [classMembers, setClassMembers] = useState<ClassMember[]>([]);
  const [classComments, setClassComments] = useState<ClassCommentRow[]>([]);
  const [classMaterials, setClassMaterials] = useState<ClassMaterialRow[]>([]);
  const [commentText, setCommentText] = useState('');
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedMeetId, setCopiedMeetId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('akis');

  const loadData = useCallback(async () => {
    if (!classId || !user) return;
    const supabase = await getSupabase();

    // 1. Sınıf bilgisi
    const { data: cData } = await supabase.from('classes').select('*').eq('id', classId).single();
    if (cData) {
      const row = cData as ClassRow;
      setClassRow(row);

      // Sınıfı kuran eğitmenin ismini profil veritabanından çek
      if (row.teacher_id) {
        const { data: tProf } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', row.teacher_id)
          .maybeSingle();

        if (tProf?.full_name) {
          setTeacherName(tProf.full_name);
        }
      }
    }

    // 2. Sınıfa ait öğrenci görevleri
    const { data: aData } = await supabase
      .from('assignments')
      .select('*')
      .eq('class_id', classId)
      .eq('student_id', user.id)
      .order('created_at', { ascending: false });
    setAssignments((aData as AssignmentRow[]) ?? []);

    // 3. Canlı dersler (Öğrenci sadece genel dersleri veya kendisine özel etütleri görür)
    const { data: lData } = await supabase
      .from('live_sessions')
      .select('*')
      .eq('class_id', classId)
      .order('created_at', { ascending: false });
    const allSessions = (lData as LiveSessionRow[]) ?? [];
    const allowedSessions = allSessions.filter(s => s.student_id === null || s.student_id === user.id);
    setLiveSessions(allowedSessions);

    // 4. Sınıf arkadaşları
    const { data: mData } = await supabase
      .from('class_students')
      .select('student_id')
      .eq('class_id', classId)
      .eq('status', 'aktif');
    const studentIds = ((mData as Pick<ClassStudentRow, 'student_id'>[]) ?? []).map(m => m.student_id);

    if (studentIds.length > 0) {
      const [{ data: snaps }, { data: profiles }] = await Promise.all([
        supabase.from('student_snapshots').select('student_id, display_name').in('student_id', studentIds),
        supabase.from('profiles').select('user_id, full_name').in('user_id', studentIds),
      ]);
      const snapMap = new Map((snaps ?? []).map((s: any) => [s.student_id, s.display_name]));
      const profileMap = new Map((profiles ?? []).map((p: any) => [p.user_id, p.full_name]));

      const members: ClassMember[] = studentIds.map(sid => {
        const sName = snapMap.get(sid);
        const pName = profileMap.get(sid);
        const raw = sName && !sName.startsWith('user_') ? sName : pName ?? sName;
        const formatted = formatStudentName(raw, 'Öğrenci');
        const isSelf = sid === user.id;
        const displayName = isSelf ? formatted : maskStudentName(formatted);
        return { student_id: sid, display_name: displayName };
      });
      setClassMembers(members);
    }

    // 5. Sınıf panosu yorumları
    const { data: commentData } = await supabase
      .from('class_comments')
      .select('*')
      .eq('class_id', classId)
      .is('assignment_id', null)
      .order('created_at', { ascending: false });
    setClassComments((commentData as ClassCommentRow[]) ?? []);

    // 6. Sınıf materyalleri
    const { data: matData } = await supabase
      .from('class_materials')
      .select('*')
      .eq('class_id', classId)
      .order('created_at', { ascending: false });
    setClassMaterials((matData as ClassMaterialRow[]) ?? []);

    setIsLoading(false);
  }, [classId, getSupabase, user]);

  async function handlePostComment(e: React.FormEvent) {
    e.preventDefault();
    if (!commentText.trim() || !user || !classId) return;
    const text = commentText.trim();
    setCommentText('');

    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('class_comments').insert({
        class_id: classId,
        assignment_id: null,
        author_id: user.id,
        author_name: user.fullName || user.firstName || 'Öğrenci',
        author_role: 'ogrenci',
        content: text,
      });
      if (error) throw error;
      loadData();
    } catch (err) {
      console.error('Yorum ekleme hatası:', err);
      showToast('Yorum gönderilemedi. Öğretmen yorumları kapatmış olabilir.', 'error');
    }
  }

  useEffect(() => { loadData(); }, [loadData]);

  async function markAssignmentCompleted(assignmentId: string) {
    if (!user) return;
    const supabase = await getSupabase();
    await supabase
      .from('assignments')
      .update({ status: 'tamamlandi', completed_at: new Date().toISOString() })
      .eq('id', assignmentId)
      .eq('student_id', user.id);
    loadData();
  }

  function copyJoinCode() {
    if (!classRow) return;
    navigator.clipboard.writeText(classRow.join_code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 1500);
  }

  function copyMeetLink(url: string, id: string) {
    navigator.clipboard.writeText(url);
    setCopiedMeetId(id);
    setTimeout(() => setCopiedMeetId(null), 1500);
  }

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-10 max-w-7xl mx-auto">
        <div className="h-40 rounded-3xl animate-shimmer mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-3">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-20 rounded-2xl animate-shimmer" />
            ))}
          </div>
          <div className="lg:col-span-4 space-y-3">
            <div className="h-40 rounded-2xl animate-shimmer" />
            <div className="h-40 rounded-2xl animate-shimmer" />
          </div>
        </div>
      </div>
    );
  }
  if (!classRow) {
    return (
      <div className="p-6 md:p-10 max-w-md mx-auto">
        <EmptyState icon={Users} title="Sınıf bulunamadı" description="Bu sınıfa erişimin olmayabilir ya da sınıf kaldırılmış olabilir." />
      </div>
    );
  }

  const activeMeet = liveSessions.find(s => s.status === 'planlandi');

  return (
    <div className="p-4 md:p-6 lg:p-10 max-w-7xl mx-auto">
      <Helmet>
        <title>{classRow.name} — Sınıfım | Bitmiyo Sınıf</title>
        <meta name="description" content={`${classRow.name} sınıfı ödevleri, canlı dersleri ve duyuruları.`} />
      </Helmet>

      {/* Geri */}
      <Link
        to="/gorevlerim"
        className="inline-flex items-center gap-1.5 text-text-muted text-sm mb-4 hover:text-accent-600 transition-colors font-semibold"
      >
        <ArrowLeft size={14} /> Görevlerime dön
      </Link>

      {/* Pembe/Mor Gradient Büyük Banner */}
      <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-r ${getClassGradient(classRow.id)} p-6 md:p-10 shadow-2xl text-white mb-6 animate-fade-in-up`}>
        <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-white/10 blur-xl pointer-events-none" />
        <div className="relative flex items-center justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-white/20 text-white backdrop-blur-md">
                Kayıtlı Sınıfım
              </span>
            </div>
            <div className="flex items-center gap-4 mb-2">
              {getClass3DIconPath(classRow) ? (
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-white/20 backdrop-blur-md p-2 flex items-center justify-center border border-white/30 shadow-lg shrink-0">
                  <img src={getClass3DIconPath(classRow)!} alt={classRow.name} className="w-full h-full object-contain filter drop-shadow" />
                </div>
              ) : (
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center font-black text-white text-xl md:text-2xl border border-white/30 shadow-lg shrink-0">
                  {classInitials(classRow.name)}
                </div>
              )}
              <h1 className="text-3xl md:text-5xl font-black tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {classRow.name}
              </h1>
            </div>
            <p className="text-white/80 text-sm md:text-base font-semibold">
              {teacherName}{extractIconFromDescription(classRow.description).cleanDesc ? ` • ${extractIconFromDescription(classRow.description).cleanDesc}` : ''}
            </p>
          </div>

          <Button
            onClick={copyJoinCode}
            className="bg-white/15 hover:bg-white/25 text-white border-0 backdrop-blur-md shadow-none shrink-0"
          >
            <Copy size={15} /> {copiedCode ? 'Kopyalandı' : `Sınıf Kodu: ${classRow.join_code}`}
          </Button>
        </div>
      </div>

      {/* Figma 2. Görsel: Sekme Navigasyon Barı */}
      <div className="flex items-center justify-between border-b border-border mb-6 gap-2 overflow-x-auto no-scrollbar">
        <nav className="flex items-center gap-1">
          {[
            { id: 'akis', label: 'Akış', icon: MessageSquare },
            { id: 'odevler', label: 'Ödevler', icon: ClipboardList },
            { id: 'materyaller', label: 'Materyaller', icon: BookOpen },
            { id: 'kisiler', label: 'Sınıf Arkadaşları', icon: Users },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-all whitespace-nowrap ${
                  isActive
                    ? 'border-accent-500 text-accent-600 dark:text-accent-400 bg-accent-500/5'
                    : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-border/30'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Figma 2. Görsel Layout: Sol Taraf İçerik (70%) + Sağ Taraf Bilgi Paneli (30%) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* SOL KOLON (70%) */}
        <div className="lg:col-span-8 space-y-6">

          {/* AKIŞ SEKMESİ */}
          {activeTab === 'akis' && (
            <div className="space-y-6">
              {/* Canlı Ders Katılım Bannerı */}
              {activeMeet && (
                <Card className="p-5 border-blue-300 dark:border-blue-500/30 bg-gradient-to-r from-blue-600 to-indigo-600 shadow-xl text-white">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <Video size={34} className="text-white shrink-0 animate-bounce drop-shadow-md" />
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                          <p className="font-black text-xs uppercase tracking-wider text-white">
                            {activeMeet.student_id === user?.id ? 'SİZE ÖZEL BİREBİR ETÜT YAYINDA!' : 'CANLI DERS YAYINDA!'}
                          </p>
                        </div>
                        <p className="text-xs text-white/80">
                          {activeMeet.student_id === user?.id
                            ? 'Öğretmeniniz sizinle birebir etüt dersi başlattı.'
                            : 'Google Meet üzerinden canlı ders başladı.'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => copyMeetLink(activeMeet.meeting_url, activeMeet.id)}
                        className="bg-white/20 hover:bg-white/30 text-white text-xs font-bold px-3 py-2 rounded-xl backdrop-blur-md transition-all flex items-center gap-1.5"
                      >
                        <Copy size={14} />
                        {copiedMeetId === activeMeet.id ? 'Kopyalandı' : 'Link Kopyala'}
                      </button>
                      <a
                        href={activeMeet.meeting_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white text-blue-700 hover:bg-blue-50 font-black text-xs px-4 py-2 rounded-xl shadow-md transition-all flex items-center gap-1.5"
                      >
                        <ExternalLink size={14} /> Google Meet ile Katıl
                      </a>
                    </div>
                  </div>
                </Card>
              )}

              {/* Öğrenci Sınıf Panosu Yorum Yazma Kutusu */}
              <Card className="p-5 shadow-sm">
                {classRow?.students_can_comment === false ? (
                  <div className="flex items-center gap-2.5 text-text-muted text-xs font-semibold">
                    <Lock size={14} className="shrink-0" />
                    Öğretmen bu sınıfta öğrenci yorumlarını kapattı.
                  </div>
                ) : (
                  <form onSubmit={handlePostComment} className="flex gap-3">
                    <Avatar name={user?.fullName || user?.firstName || 'Öğrenci'} size={40} />
                    <div className="flex-1">
                      <textarea
                        ref={commentTextareaRef}
                        value={commentText}
                        onChange={e => setCommentText(e.target.value)}
                        placeholder="Sınıf panosuna mesaj yaz veya soru sor..."
                        rows={2}
                        className="w-full rounded-xl border border-border bg-surface p-3 text-sm outline-none focus:border-accent-500 transition-colors resize-none"
                      />
                      <div className="flex justify-end items-center gap-2 mt-2">
                        <EmojiPickerButton textareaRef={commentTextareaRef} value={commentText} onChange={setCommentText} />
                        <Button
                          type="submit"
                          size="sm"
                          disabled={!commentText.trim()}
                          className="bg-accent-600 hover:bg-accent-700 text-white"
                        >
                          <Send size={14} /> Gönder
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
              </Card>

              {/* Geçmiş Canlı Dersler & Kayıtlar */}
              {liveSessions.filter(s => s.status === 'tamamlandi').length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary flex items-center gap-1.5">
                    <Video size={14} className="text-blue-600" /> Geçmiş Canlı Dersler & Kayıtlar
                  </h3>
                  {liveSessions.filter(s => s.status === 'tamamlandi').map(s => (
                    <Card key={s.id} className="p-4 border-blue-200 dark:border-blue-500/20 bg-blue-500/5">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                          <Video size={24} className="text-blue-600 shrink-0" />
                          <div>
                            <p className="font-bold text-sm text-text-primary">Google Meet Canlı Ders (Tamamlandı)</p>
                            <p className="text-xs text-text-muted">
                              Tarih: {new Date(s.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>

                        <span className="text-xs font-bold text-text-muted bg-surface px-2.5 py-1 rounded-lg border border-border">
                          Tamamlandı
                        </span>
                      </div>

                      {s.notes && (
                        <div className="mt-3 pt-2 border-t border-border/60">
                          <p className="text-xs text-text-secondary italic">
                            <strong>Öğretmen Notu:</strong> {s.notes}
                          </p>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
              {/* Zaman tüneli — duyurular + ödevler tek, tarih sıralı akışta (sabitlenmiş
                  ödevler her zaman en üstte; eskiden duyurular ayrı, sabit bir blok
                  olarak her zaman en üstte kalıyordu, bu bug buradaydı) */}
              <div className="space-y-3">
                <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary">
                  Son Paylaşımlar
                </h3>

                {assignments.length === 0 && classComments.length === 0 ? (
                  <EmptyState
                    icon={ClipboardList}
                    title="Sınıfta henüz paylaşım yok"
                    description="Öğretmeniniz görev veya duyuru paylaştığında burada görüntülenecektir."
                  />
                ) : (
                  [
                    ...assignments.map(a => ({ kind: 'assignment' as const, ts: a.created_at, pinned: Boolean(a.is_pinned), data: a })),
                    ...classComments.map(c => ({ kind: 'comment' as const, ts: c.created_at, pinned: false, data: c })),
                  ]
                    .sort((x, y) => {
                      if (x.pinned !== y.pinned) return x.pinned ? -1 : 1;
                      return new Date(y.ts).getTime() - new Date(x.ts).getTime();
                    })
                    .map((item, idx) => {
                      const delayStyle = { animationDelay: `${Math.min(idx, 8) * 40}ms` };
                      if (item.kind === 'comment') {
                        const c = item.data;
                        const isTeacher = c.author_role === 'ogretmen';
                        const isSelf = user ? c.author_id === user.id : false;
                        const authorDisplayName = isTeacher || isSelf
                          ? c.author_name
                          : maskStudentName(c.author_name);
                        return (
                          <Card key={`c-${c.id}`} className="p-4 bg-surface border border-border animate-fade-in-up" style={delayStyle}>
                            <div className="flex items-start gap-3">
                              <Avatar name={authorDisplayName} size={36} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <p className="font-bold text-sm text-text-primary">{authorDisplayName}</p>
                                    {c.author_role === 'ogretmen' && (
                                      <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-accent-500/10 text-accent-600">
                                        Öğretmen
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[11px] text-text-muted">
                                    {new Date(c.created_at).toLocaleDateString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <p className="text-sm text-text-secondary mt-1 whitespace-pre-wrap"><EmoteText text={c.content} /></p>
                                <ReactionBar contentType="class_comment" contentId={c.id} />
                              </div>
                            </div>
                          </Card>
                        );
                      }
                      const a = item.data;
                      const isDone = a.status === 'tamamlandi';
                      const isPinned = item.pinned;
                      const targetUrl = getAssignmentUrl(a.type, a.resource_ref);
                      return (
                        <Card
                          key={a.id}
                          className={`p-5 flex items-center gap-4 transition-all animate-fade-in-up ${
                            isPinned
                              ? 'border-purple-300 dark:border-purple-500/40 bg-gradient-to-r from-purple-500/10 via-pink-500/5 to-transparent shadow-md'
                              : isDone
                              ? 'opacity-60 bg-surface/50'
                              : 'hover:border-accent-300 shadow-sm'
                          }`}
                          style={delayStyle}
                        >
                          <div className={`shrink-0 ${
                            isPinned
                              ? 'text-purple-600'
                              : isDone
                              ? 'text-success-500'
                              : a.type === 'video'
                              ? 'text-accent-600'
                              : a.type === 'test'
                              ? 'text-purple-600'
                              : 'text-text-secondary'
                          }`}>
                            {isPinned ? <Pin size={28} className="fill-current" /> : isDone ? <Check size={26} /> : a.type === 'video' ? <Video size={26} /> : a.type === 'test' ? <Target size={26} /> : <BookOpen size={26} />}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {isPinned && (
                                <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-purple-600 text-white">
                                  SABİTLENMİŞ YENİ ÖDEV
                                </span>
                              )}
                              <p className={`font-bold text-sm truncate ${isDone ? 'line-through text-text-muted' : 'text-text-primary'}`}>
                                {a.title}
                              </p>
                              {isDone && <Badge variant="success">Tamamlandı</Badge>}
                              {a.type === 'test' && !isDone && <Badge variant="accent">Test Ödevi</Badge>}
                            </div>

                            {a.due_date && (
                              <div className="flex items-center gap-1 text-text-muted text-xs">
                                <Clock size={11} /> Teslim: {formatTurkishDueDate(a.due_date)}
                              </div>
                            )}

                            {targetUrl && (
                              <a
                                href={targetUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`text-xs mt-2 inline-flex items-center gap-1 font-bold px-3 py-1.5 rounded-xl transition-all border ${
                                  a.type === 'test'
                                    ? 'bg-purple-500/10 text-purple-600 border-purple-200 hover:bg-purple-500/20'
                                    : 'bg-accent-500/10 text-accent-600 border-accent-200 hover:bg-accent-500/20'
                                }`}
                              >
                                <ExternalLink size={12} />
                                {a.type === 'test' ? 'Testi Çöz (soru.bitmiyo.com.tr)' : a.type === 'video' ? 'Videoyu İzle' : 'Bağlantıyı Aç'}
                              </a>
                            )}
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <Link
                              to={`/sinif/${classId}/odev/${a.id}`}
                              className="bg-accent-600 hover:bg-accent-700 text-white text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1"
                            >
                              Detay
                            </Link>

                            {!isDone && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => markAssignmentCompleted(a.id)}
                              >
                                <Check size={14} /> Tamamlandı
                              </Button>
                            )}
                          </div>
                        </Card>
                      );
                    })
                )}
              </div>
            </div>
          )}

          {/* ÖDEVLER SEKMESİ */}
          {activeTab === 'odevler' && (
            <div className="space-y-4">
              <h3 className="text-sm font-black uppercase tracking-widest text-text-secondary">
                Tüm Sınıf Görevlerim ({assignments.length})
              </h3>
              {assignments.length === 0 ? (
                <EmptyState
                  icon={ClipboardList}
                  title="Bu sınıfta henüz görevin yok"
                  description="Öğretmenin bir görev atadığında burada listelenecek."
                />
              ) : (
                <div className="space-y-3">
                  {assignments.map((a, i) => (
                    <Card
                      key={a.id}
                      className="p-4 flex items-center justify-between gap-4 animate-fade-in-up"
                      style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="text-accent-600 shrink-0">
                          {a.type === 'video' ? <Video size={22} /> : a.type === 'test' ? <Target size={22} /> : <FileText size={22} />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-sm text-text-primary truncate">{a.title}</p>
                          <p className="text-xs text-text-muted">
                            {a.due_date ? `Son Teslim: ${formatTurkishDueDate(a.due_date)}` : 'Süresiz'}
                          </p>
                        </div>
                      </div>
                      <Link
                        to={`/sinif/${classId}/odev/${a.id}`}
                        className="bg-accent-600 hover:bg-accent-700 text-white text-xs font-bold px-3 py-2 rounded-xl shrink-0"
                      >
                        Detay Gör
                      </Link>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* MATERYALLER SEKMESİ */}
          {activeTab === 'materyaller' && (
            <div className="space-y-4">
              <h3 className="text-sm font-black uppercase tracking-widest text-text-secondary">
                Ders Ekleri & Materyaller ({classMaterials.length})
              </h3>
              {classMaterials.length === 0 ? (
                <EmptyState
                  icon={BookOpen}
                  title="Henüz materyal eklenmedi"
                  description="Öğretmeniniz ders notu veya dosya paylaştığında burada yüklenecektir."
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {classMaterials.map((m, i) => (
                    <Card
                      key={m.id}
                      className="p-4 flex items-center justify-between gap-3 shadow-sm animate-fade-in-up"
                      style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center font-black text-xs shrink-0">
                          {m.file_type}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-sm text-text-primary truncate">{m.title}</p>
                          <p className="text-xs text-text-muted truncate">{m.description || m.file_size}</p>
                        </div>
                      </div>
                      <a
                        href={m.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-accent-600 hover:bg-accent-700 text-white font-bold text-xs px-3.5 py-2 rounded-xl flex items-center gap-1.5 shrink-0"
                      >
                        <ExternalLink size={14} /> Aç
                      </a>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* KİŞİLER SEKMESİ */}
          {activeTab === 'kisiler' && (() => {
            const classmates = classMembers.filter(m => m.student_id !== user?.id);
            return (
              <div className="space-y-4">
                <h3 className="text-sm font-black uppercase tracking-widest text-text-secondary">
                  Sınıf Arkadaşları ({classmates.length})
                </h3>
                {classmates.length === 0 ? (
                  <EmptyState
                    icon={Users}
                    title="Henüz sınıf arkadaşın yok"
                    description="Diğer öğrenciler sınıfa katıldıkça burada listelenecek."
                  />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {classmates.map((m, i) => (
                      <Card
                        key={m.student_id}
                        className="p-3.5 flex items-center gap-3 animate-fade-in-up"
                        style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                      >
                        <Avatar name={m.display_name} size={36} />
                        <span className="font-bold text-sm text-text-primary">{m.display_name}</span>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

        </div>

        {/* SAĞ KOLON (30% - Figma 6. Görsel Sağ Panel) */}
        <div className="lg:col-span-4 space-y-6">

          {/* Yaklaşan Ödevler Kartı & Haftalık Takvim */}
          <UpcomingAssignmentsCard
            assignments={assignments}
            liveSessions={liveSessions}
            classId={classId}
            isTeacher={false}
          />

          {/* Figma 6. Görsel: Sınıf Bilgileri Kartı */}
          <Card className="p-6 space-y-4 shadow-sm">
            <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary border-b border-border pb-3">
              Sınıf Bilgileri
            </h3>
            
            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-text-muted font-medium">Eğitmen:</span>
                <strong className="text-text-primary font-bold">{teacherName}</strong>
              </div>

              {extractIconFromDescription(classRow.description).cleanDesc && (
                <div className="flex items-center justify-between pt-2 border-t border-border gap-3">
                  <span className="text-text-muted font-medium shrink-0">Açıklama:</span>
                  <span className="text-text-primary font-bold text-right">{extractIconFromDescription(classRow.description).cleanDesc}</span>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-text-muted font-medium">Sınıf Kodu:</span>
                <span className="font-mono font-black text-sm text-accent-600 bg-accent-500/10 px-2 py-0.5 rounded-lg tracking-wider">
                  {classRow.join_code}
                </span>
              </div>
            </div>
          </Card>

          {/* Figma 6. Görsel: Sınıf İstatistikleri (4 Mini Kart Grid: Öğrenci, Ödev, Materyal, A- Ortalama) */}
          <Card className="p-6 space-y-4 shadow-sm">
            <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary border-b border-border pb-3">
              Sınıf İstatistikleri
            </h3>

            <div className="grid grid-cols-2 gap-2.5 text-center">
              <div className="p-3 rounded-2xl bg-surface border border-border">
                <p className="text-2xl font-black text-text-primary" style={{ fontFamily: 'var(--font-heading)' }}>
                  {classMembers.length}
                </p>
                <p className="text-[10px] font-bold text-text-muted mt-0.5">Öğrenci</p>
              </div>

              <div className="p-3 rounded-2xl bg-surface border border-border">
                <p className="text-2xl font-black text-accent-600" style={{ fontFamily: 'var(--font-heading)' }}>
                  {assignments.length}
                </p>
                <p className="text-[10px] font-bold text-text-muted mt-0.5">Ödev</p>
              </div>

              <div className="p-3 rounded-2xl bg-surface border border-border">
                <p className="text-2xl font-black text-purple-600" style={{ fontFamily: 'var(--font-heading)' }}>
                  {classMaterials.length}
                </p>
                <p className="text-[10px] font-bold text-text-muted mt-0.5">Materyal</p>
              </div>

              <div className="p-3 rounded-2xl bg-success-100 border border-success-500/20">
                <p className="text-2xl font-black text-success-500" style={{ fontFamily: 'var(--font-heading)' }}>
                  %{assignments.length > 0 ? Math.round((assignments.filter(a => a.status === 'tamamlandi').length / assignments.length) * 100) : 100}
                </p>
                <p className="text-[10px] font-bold text-success-500 mt-0.5">Tamamlanma</p>
              </div>
            </div>

            {assignments.length > 0 && (
              <div className="pt-1">
                <div className="h-1.5 rounded-full bg-border overflow-hidden">
                  <div
                    className="h-full rounded-full bg-success-500 transition-all duration-500"
                    style={{ width: `${Math.round((assignments.filter(a => a.status === 'tamamlandi').length / assignments.length) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </Card>

        </div>

      </div>
    </div>
  );
}
